import i18next from 'i18next';
import setGlobalState, {pushToGlobalState, unsetGlobalState} from '../setGlobalState';
import request from '../../library/request';
import store from '../../store';
import {bounceMessage} from '../../layout/userMessages/UserMessage';
import {CreateLecturePayload, THall, TLecturer, TSlot, TTimetableItem} from './timetable.type';
import { getStartOfDay } from '../../library/dates';

const getEid = () => store.getState().event.eid;

const findLectureDay = (lectures: Record<number, TTimetableItem[]>, ttid: number): [string, TTimetableItem[]] | null => {
    const entry = (Object.entries(lectures) as [string, TTimetableItem[]][]).find(([, items]) => items.some(l => l.ttid === ttid));
    return entry || null;
};

export const getTimetable = async (date: number) => {
    const xhrByDay = store.getState().timetable.getLecturesXHR;
    if(xhrByDay[date] === 'LOADING' || xhrByDay[date] === 'SUCCESS') return;

    const limit = 10;
    setGlobalState('timetable.getLecturesXHR', {...xhrByDay, [date]: 'LOADING'});

    try {
        let fetching = true;
        while(fetching) {
            const currentLectures = store.getState().timetable.lectures[date] || [];
            const res = await request('timetable.getTimetable', 'get', `/event/${getEid()}/timetable`, {date, limit, offset: currentLectures.length});
            if(res?.lectures) {
                setGlobalState('timetable.lectures', {...store.getState().timetable.lectures, [date]: [...currentLectures, ...res.lectures]});
                fetching = res.lectures.length >= limit;
            } else {
                fetching = false;
            }
        }
        setGlobalState('timetable.getLecturesXHR', {...store.getState().timetable.getLecturesXHR, [date]: 'SUCCESS'});
    } catch {
        setGlobalState('timetable.getLecturesXHR', {...store.getState().timetable.getLecturesXHR, [date]: 'FAILED'});
    }
};

export const moveLecture = async (ttid: number, newTime: number) => {
    const res = await request('timetable.moveLecture', 'put', `/event/${getEid()}/timetable/${ttid}`, {time: newTime});

    if(res) {
        const currentLectures = store.getState().timetable.lectures;
        const day = findLectureDay(currentLectures, ttid);
        if(day) {
            const [dayTs, dayLectures] = day;
            const index = dayLectures.findIndex(l => l.ttid === ttid);
            if(index !== -1) setGlobalState(`timetable.lectures[${dayTs}][${index}]`, {...dayLectures[index], time: newTime});
        }
    }
};

export const getLecturers = async () => {
    const res = await request('timetable.getLecturers', 'get', `/event/${getEid()}/lecturer`);
    if(res?.lecturers) setGlobalState('timetable.lecturers', res.lecturers);
};

export const createLecturer = async (payload: Omit<TLecturer, 'lid'>): Promise<TLecturer | null> => {
    const res = await request('timetable.createLecturer', 'post', `/event/${getEid()}/lecturer`, payload);
    if(!res?.lecturer) return null;
    pushToGlobalState('timetable.lecturers', res.lecturer);
    return res.lecturer;
};

export const getHalls = async () => {
    const res = await request('timetable.getHalls', 'get', `/event/${getEid()}/timetableHall`);
    if(res?.halls) setGlobalState('timetable.halls', res.halls);
};

export const createHall = async (title: string): Promise<THall | null> => {
    const res = await request('timetable.createHall', 'post', `/event/${getEid()}/timetableHall`, {title});
    if(!res?.hall) return null;
    pushToGlobalState('timetable.halls', res.hall);
    return res.hall;
};

export const getSlots = async () => {
    const res = await request('timetable.getSlots', 'get', `/event/${getEid()}/timetableSlot`);
    if(res?.slots) setGlobalState('timetable.slots', res.slots);
};

export const createSlot = async (timeStart: number): Promise<TSlot | null> => {
    const res = await request('timetable.createSlot', 'post', `/event/${getEid()}/timetableSlot`, {timeStart});
    if(!res?.slot) return null;
    const slots = store.getState().timetable.slots;
    if(!slots.some((s: TSlot) => s.tsid === res.slot.tsid)) {
        setGlobalState('timetable.slots', [...slots, res.slot].sort((a: TSlot, b: TSlot) => a.timeStart - b.timeStart));
    }
    return res.slot;
};

export const createLecture = async (payload: CreateLecturePayload): Promise<TTimetableItem | null> => {
    const res = await request('timetable.createLecture', 'post', `/event/${getEid()}/timetable`, payload);
    if(!res?.lecture) return null;

    const lecture = {...res.lecture, time: payload.time} as TTimetableItem;
    const dayKey = getStartOfDay(payload.time);
    const lectures = store.getState().timetable.lectures;
    const dayLectures = lectures[dayKey] || [];
    const updated = [...dayLectures, lecture].sort((a, b) => a.time - b.time);
    setGlobalState('timetable.lectures', {...lectures, [dayKey]: updated});
    return lecture;
};

export const updateLecture = async (ttid: number, payload: Partial<CreateLecturePayload>): Promise<TTimetableItem | null> => {
    const res = await request('timetable.updateLecture', 'put', `/event/${getEid()}/timetable/${ttid}`, payload);
    if(!res?.lecture) return null;

    const lecture = res.lecture as TTimetableItem;
    const dayKey = getStartOfDay(lecture.time);
    const entries = Object.entries(store.getState().timetable.lectures) as [string, TTimetableItem[]][];
    const next: Record<string, TTimetableItem[]> = Object.fromEntries(entries.map(([k, arr]) => [k, arr.filter(l => l.ttid !== ttid)]));
    next[dayKey] = [...(next[dayKey] || []), lecture].sort((a, b) => a.time - b.time);
    setGlobalState('timetable.lectures', next);
    bounceMessage(i18next.t('timetable:lectureUpdated'), 'success');
    return lecture;
};
export const deleteLecture = async (ttid: number) => {
    const res = await request('timetable.deleteLecture', 'delete', `/event/${getEid()}/timetable/${ttid}`);

    if(res) {
        const currentLectures = store.getState().timetable.lectures;
        const day = findLectureDay(currentLectures, ttid);

        if(day) {
            const [dayTs, dayLectures] = day;
            const index = dayLectures.findIndex(l => l.ttid === ttid);
            if(index !== -1) unsetGlobalState(`timetable.lectures[${dayTs}][${index}]`);
        }
        bounceMessage(i18next.t('timetable:lectureDeleted'), 'success');
    }
};

export const duplicateLecture = async (ttid: number) => {
    const res = await request('timetable.duplicateLecture', 'post', `/event/${getEid()}/timetable/${ttid}/duplicate`);

    if(res?.ttid) {
        const currentLectures = store.getState().timetable.lectures;
        const day = findLectureDay(currentLectures, ttid);

        if(day) {
            const [dayTs] = day;
            pushToGlobalState(`timetable.lectures[${dayTs}]`, res);
        }
        bounceMessage(i18next.t('timetable:lectureDuplicated'), 'success');
    }
};

